/**
 * Snowcubes Gift Card Core
 * - Small, testable logic + thin DOM helpers
 * - Safe debug logging behind flag (?sc_debug_gift=1 or localStorage.sc_debug_gift=1)
 *
 * This file is loaded globally from `layout/theme.liquid` so other theme assets
 * (e.g. `product-form.js`, `gift-box-customizer-modal.js`) can depend on it.
 */

(function initSnowcubesGiftCardCore(global) {
  const GIFT_CONFIG_SCHEMA_V1 = 'snowcubes.giftCard.v1';
  const GIFT_CONFIG_STORAGE_KEY_PREFIX = 'sc_gift_config_prototype';

  // Scope prototype key per product to prevent cross-product config bleed
  function getProductHandle() {
    try {
      const match = global.location?.pathname?.match(/\/products\/([^/?#]+)/);
      return match ? match[1] : null;
    } catch (_) {
      return null;
    }
  }

  function getGiftConfigStorageKey() {
    const handle = getProductHandle();
    return handle
      ? `${GIFT_CONFIG_STORAGE_KEY_PREFIX}:${handle}`
      : GIFT_CONFIG_STORAGE_KEY_PREFIX;
  }
  const GIFT_UI_FLAG_KEY = 'sc_gift_ui';
  const DEBUG_FLAG_KEY = 'sc_debug_gift';

  function safeGetSearchParams() {
    try {
      return new URLSearchParams(global.location?.search || '');
    } catch (_) {
      return new URLSearchParams('');
    }
  }

  function safeGetLocalStorageItem(key) {
    try {
      return global.localStorage?.getItem(key);
    } catch (_) {
      return null;
    }
  }

  function safeSetLocalStorageItem(key, value) {
    try {
      global.localStorage?.setItem(key, value);
      return true;
    } catch (_) {
      return false;
    }
  }

  function safeRemoveLocalStorageItem(key) {
    try {
      global.localStorage?.removeItem(key);
      return true;
    } catch (_) {
      return false;
    }
  }

  function isFlagEnabled(flagKey) {
    if (global.SnowcubesFlags?.isEnabled) {
      return global.SnowcubesFlags.isEnabled(flagKey);
    }
    // Inline fallback if feature-flags.js hasn't loaded yet.
    const params = safeGetSearchParams();
    if (params.get(flagKey) === '1') {
      safeSetLocalStorageItem(flagKey, '1');
      return true;
    }
    return safeGetLocalStorageItem(flagKey) === '1';
  }

  function isDebugEnabled() {
    if (global.SnowcubesFlags?.isEnabled) {
      return global.SnowcubesFlags.isEnabled(DEBUG_FLAG_KEY);
    }
    return isFlagEnabled(DEBUG_FLAG_KEY);
  }

  function debug(event, data) {
    if (!isDebugEnabled()) return;
    if (data === undefined) {
      // eslint-disable-next-line no-console
      console.log('[Gift] ' + event);
      return;
    }
    // eslint-disable-next-line no-console
    console.log('[Gift] ' + event, data);
  }

  function isGiftUIEnabled() {
    return true;
  }

  function safeJsonParse(raw) {
    try {
      return { ok: true, value: JSON.parse(raw) };
    } catch (error) {
      return { ok: false, error };
    }
  }

  function isPlainObject(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  function safeParseGiftConfig(raw) {
    if (!raw || typeof raw !== 'string') return { ok: false, error: new Error('empty') };
    const parsed = safeJsonParse(raw);
    if (!parsed.ok) return parsed;
    if (!isPlainObject(parsed.value)) {
      return { ok: false, error: new Error('not_object') };
    }
    return { ok: true, value: parsed.value };
  }

  // Self-imposed guard on the serialized _GiftCardConfig line-item property.
  //
  // VERIFIED 2026-06-25 against Shopify's AJAX Cart docs: there is NO documented
  // length/byte limit for line-item property VALUES. The 500 below is a
  // conservative self-imposed budget to avoid any truncation on /cart/add — not a
  // platform constant. This check is CHARACTER-based (`value.length` = UTF-16
  // units), which matches the 200-CHAR gift-message input in
  // snippets/gift-card-recipient-form.liquid.
  //
  // ⚠️ DO NOT switch this to UTF-8 byte counting without testing a live cart
  // first. A 200-char message in Chinese (~3 bytes/char) or emoji (4 bytes) is
  // well within the char budget but ~600+ bytes, so byte-counting at 450 would
  // WRONGLY reject valid gift notes — and Chinese / emoji messages are a core
  // Snowcubes use case. The constant NAME says "BYTES" for historical reasons;
  // the semantics here are CHARS.
  const GIFT_CONFIG_MAX_BYTES = 450; // chars (UTF-16 length), NOT bytes — see note above

  function serializeGiftConfig(config) {
    try {
      const value = JSON.stringify(config);
      if (typeof value === 'string' && value.length > GIFT_CONFIG_MAX_BYTES) {
        return { ok: false, error: new Error('too_large') };
      }
      return { ok: true, value };
    } catch (error) {
      return { ok: false, error };
    }
  }

  function summarizeGiftConfig(config) {
    const c = isPlainObject(config) ? config : {};
    const to = typeof c.to === 'string' ? c.to : '';
    const from = typeof c.from === 'string' ? c.from : '';
    const message = typeof c.message === 'string' ? c.message : '';
    const cardId = typeof c.cardId === 'string' ? c.cardId : '';
    const schema = typeof c.schema === 'string' ? c.schema : '';
    const font = typeof c.font === 'string' ? c.font : '';

    return {
      schema,
      cardId,
      font,
      toLength: to.length,
      fromLength: from.length,
      messageLength: message.length,
    };
  }

  function isGiftWrappedVariantTitle(variantTitle) {
    const title = (variantTitle || '').toLowerCase();
    return /\bgift[-\s]*wrap(ped)?\b/.test(title);
  }

  // =========================
  // Gift config prototype store
  // =========================
  function saveGiftConfigPrototype(config) {
    const serialized = serializeGiftConfig(config);
    if (!serialized.ok) return false;
    return safeSetLocalStorageItem(getGiftConfigStorageKey(), serialized.value);
  }

  function loadGiftConfigPrototype() {
    const raw = safeGetLocalStorageItem(getGiftConfigStorageKey());
    if (!raw) return null;
    const parsed = safeParseGiftConfig(raw);
    if (!parsed.ok) return null;
    return parsed.value;
  }

  function clearGiftConfigPrototype() {
    return safeRemoveLocalStorageItem(getGiftConfigStorageKey());
  }

  /** Remove ALL per-product gift config keys (used on checkout). */
  function clearAllGiftConfigPrototypes() {
    try {
      Object.keys(global.localStorage || {})
        .filter(function (k) { return k.startsWith(GIFT_CONFIG_STORAGE_KEY_PREFIX); })
        .forEach(function (k) { global.localStorage.removeItem(k); });
      return true;
    } catch (_) {
      return false;
    }
  }

  function upsertHiddenInput(form, name, value) {
    if (!form) return null;
    let input = form.querySelector(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
    return input;
  }

  function upsertGiftInputs(form, config) {
    const serialized = serializeGiftConfig(config);
    if (!serialized.ok) return { ok: false, error: serialized.error };

    const cfg = isPlainObject(config) ? config : {};
    upsertHiddenInput(form, 'properties[_GiftCardConfig]', serialized.value);
    upsertHiddenInput(form, 'properties[_GiftCardTo]', (cfg.to || '').toString());
    upsertHiddenInput(form, 'properties[_GiftCardFrom]', (cfg.from || '').toString());
    upsertHiddenInput(form, 'properties[_GiftCardMessage]', (cfg.message || '').toString());

    return { ok: true };
  }

  // =========================
  // Gift note card model (pure)
  // =========================
  function formatGiftSummaryText(config) {
    if (!isPlainObject(config)) return '';
    return 'Gift note';
  }

  function formatGiftMessagePreview(config, { maxChars = 72 } = {}) {
    if (!isPlainObject(config)) return '';
    const raw = (config.message || '').toString().trim();
    if (!raw) return '';

    // Collapse whitespace/newlines into single spaces for compact preview.
    const normalized = raw.replace(/\s+/g, ' ').trim();
    if (!normalized) return '';

    const clipped =
      normalized.length > maxChars
        ? `${normalized.slice(0, maxChars).trimEnd()}…`
        : normalized;

    return clipped;
  }

  function deriveGiftNoteCardModel({
    giftUIEnabled,
    variantTitle,
    quantity,
    config,
    giftAlwaysOn,
    configAppliesToCurrentVariant = true,
  }) {
    const isGiftWrapped = isGiftWrappedVariantTitle(variantTitle);
    const effectiveConfig = configAppliesToCurrentVariant ? config : null;
    const hasConfig = !!effectiveConfig;
    const message = isPlainObject(effectiveConfig) ? (effectiveConfig.message || '').toString().trim() : '';
    const hasMessage = !!message;
    const shouldShowCard = !!giftUIEnabled && (isGiftWrapped || !!giftAlwaysOn);

    const state = hasConfig ? (hasMessage ? 'filled' : 'blank') : 'empty';

    const titleText = state === 'empty' ? 'Write your personal card' : 'Gift note';

    const statusText =
      state === 'filled'
        ? 'Added'
        : state === 'blank'
          ? 'Left blank'
          : 'Design yours';

    const statusClass = state === 'empty' ? 'is-cta' : '';

    const descriptionText =
      state === 'filled'
        ? 'Edit your note.'
        : state === 'blank'
          ? 'Left blank — perfect if you want to write by hand.'
          : 'Pick a card design and write your note — or leave it blank to write by hand.';

    const showIcon = state === 'empty';

    const summaryText = state === 'filled' && effectiveConfig
      ? formatGiftMessagePreview(effectiveConfig, { maxChars: 96 })
      : '';

    return {
      shouldShowCard,
      isGiftWrapped,
      state,
      titleText,
      statusText,
      statusClass,
      descriptionText,
      summaryText,
      showIcon,
      addButtonHidden: hasConfig,
      addButtonText: 'Add gift note',
      editButtonHidden: !hasConfig,
    };
  }

  // =========================
  // Gift note card DOM updater
  // (kept minimal + safe)
  // =========================
  const GIFT_NOTE_CARD_ANIMATION_MS = 260;
  let giftNoteCardHideTimeoutId = null;
  let giftNoteCardShowRaf1 = null;
  let giftNoteCardShowRaf2 = null;

  function clearGiftNoteCardTimers() {
    if (giftNoteCardHideTimeoutId) {
      clearTimeout(giftNoteCardHideTimeoutId);
      giftNoteCardHideTimeoutId = null;
    }
    if (giftNoteCardShowRaf1) {
      cancelAnimationFrame(giftNoteCardShowRaf1);
      giftNoteCardShowRaf1 = null;
    }
    if (giftNoteCardShowRaf2) {
      cancelAnimationFrame(giftNoteCardShowRaf2);
      giftNoteCardShowRaf2 = null;
    }
  }

  function setGiftNoteCardVisibility(card, shouldShow, { animate = true } = {}) {
    if (!card) return;

    const prefersReducedMotion =
      typeof global.matchMedia === 'function' &&
      global.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const shouldAnimate = animate && !prefersReducedMotion;

    // Fully hidden: nothing to do.
    if (!shouldShow && card.hidden) return;

    // Fully shown: nothing to do.
    const isExpanded = !card.hidden && !card.classList.contains('is-collapsed');
    if (shouldShow && isExpanded) return;

    clearGiftNoteCardTimers();

    if (shouldShow) {
      card.hidden = false;
      card.setAttribute('aria-hidden', 'false');
      card.classList.add('is-collapsed');
      if ('inert' in card) card.inert = true;

      if (!shouldAnimate) {
        card.classList.remove('is-collapsed');
        if ('inert' in card) card.inert = false;
        return;
      }

      giftNoteCardShowRaf1 = requestAnimationFrame(() => {
        giftNoteCardShowRaf2 = requestAnimationFrame(() => {
          card.classList.remove('is-collapsed');
          if ('inert' in card) card.inert = false;
        });
      });
      return;
    }

    // Hide (collapse first, then set [hidden] after transition)
    card.classList.add('is-collapsed');
    card.setAttribute('aria-hidden', 'true');
    if ('inert' in card) card.inert = true;

    if (!shouldAnimate) {
      card.hidden = true;
      return;
    }

    giftNoteCardHideTimeoutId = setTimeout(() => {
      card.hidden = true;
    }, GIFT_NOTE_CARD_ANIMATION_MS);
  }

  function applyGiftNoteCardModel(card, model, { animate = true } = {}) {
    if (!card || !model) return;

    setGiftNoteCardVisibility(card, model.shouldShowCard, { animate });
    if (!model.shouldShowCard) return;

    const statusEl = card.querySelector('[data-gift-note-status]');
    const descriptionEl = card.querySelector('[data-gift-note-description]');
    const summaryEl = card.querySelector('[data-gift-note-summary]');
    const ctaBtn = card.querySelector('[data-gift-note-open]');
    const titleEl = card.querySelector('.gift-edit-btn__title');
    const iconEl = card.querySelector('[data-gift-note-icon]');

    if (titleEl) titleEl.textContent = model.titleText || '';

    if (iconEl) {
      // SVGElement.hidden doesn't reflect to the HTML [hidden] attribute in all
      // browsers, so CSS selectors like [hidden] { display:none } won't match.
      // Use setAttribute/removeAttribute to guarantee the attribute is present.
      if (model.showIcon) {
        iconEl.removeAttribute('hidden');
      } else {
        iconEl.setAttribute('hidden', '');
      }
    }

    if (ctaBtn) {
      ctaBtn.classList.toggle('gift-edit-btn--has-icon', !!model.showIcon);
    }

    if (statusEl) {
      statusEl.classList.remove('is-required', 'is-complete', 'is-cta');
      statusEl.textContent = model.statusText || '';
      if (model.statusClass) statusEl.classList.add(model.statusClass);
      if (model.state === 'empty') statusEl.classList.add('is-required');
      if (model.state === 'filled') statusEl.classList.add('is-complete');
    }

    if (descriptionEl) descriptionEl.textContent = model.descriptionText || '';

    if (summaryEl) {
      if (model.state === 'filled' && model.summaryText) {
        summaryEl.textContent = model.summaryText;
        summaryEl.hidden = false;
      } else {
        summaryEl.textContent = '';
        summaryEl.hidden = true;
      }
    }

    // Prefer the message preview when present; otherwise show the helper copy.
    if (descriptionEl) {
      descriptionEl.hidden = model.state === 'filled' && !!model.summaryText;
    }

    if (ctaBtn) {
      const mode = model.state === 'empty' ? 'add' : 'edit';
      ctaBtn.dataset.mode = mode;
      ctaBtn.setAttribute(
        'aria-label',
        mode === 'add' ? 'Add gift note' : 'Edit gift note'
      );
    }
  }

  function getCurrentVariantTitleFromDOM() {
    const variantSelect = document.querySelector('variant-selects, variant-radios');
    const title = variantSelect?.currentVariant?.title;
    if (title) return title;

    if (!variantSelect) return '';

    const selectValues = Array.from(variantSelect.querySelectorAll('select'))
      .map((select) => select.value)
      .filter(Boolean);
    if (selectValues.length) return selectValues.join(' / ');

    const radioValues = Array.from(variantSelect.querySelectorAll('input[type="radio"]:checked'))
      .map((radio) => radio.value)
      .filter(Boolean);
    return radioValues.join(' / ');
  }

  function getCurrentQuantityFromDOM() {
    const quantityInput = document.querySelector(
      'product-form input[name="quantity"], input[name="quantity"]'
    );
    const quantity = parseInt(quantityInput?.value, 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  function updateGiftNoteCardState({ animate = true } = {}) {
    const card = document.querySelector('[data-gift-note-card]');
    if (!card) return;

    const config = loadGiftConfigPrototype();
    const variantTitle = getCurrentVariantTitleFromDOM();
    const quantity = getCurrentQuantityFromDOM();
    const giftUIEnabled = true;
    const giftAlwaysOn = card.hasAttribute('data-gift-always-on');

    const model = deriveGiftNoteCardModel({
      giftUIEnabled,
      variantTitle,
      quantity,
      config,
      giftAlwaysOn,
    });

    debug('updateGiftNoteCardState', {
      giftUIEnabled,
      variantTitle,
      quantity,
      state: model.state,
      shouldShowCard: model.shouldShowCard,
      hasConfig: !!config,
    });

    applyGiftNoteCardModel(card, model, { animate });
  }

  function updateGiftNoteCardStateScoped({ card, variantTitle, quantity, productHandle, animate = true }) {
    if (!card) return;

    const storageKey = productHandle
      ? `${GIFT_CONFIG_STORAGE_KEY_PREFIX}:${productHandle}`
      : GIFT_CONFIG_STORAGE_KEY_PREFIX;
    const raw = safeGetLocalStorageItem(storageKey);
    const config = raw ? (function() {
      const parsed = safeParseGiftConfig(raw);
      return parsed.ok ? parsed.value : null;
    })() : null;

    const giftUIEnabled = true;
    const giftAlwaysOn = card.hasAttribute('data-gift-always-on');
    const model = deriveGiftNoteCardModel({ giftUIEnabled, variantTitle, quantity, config, giftAlwaysOn });
    applyGiftNoteCardModel(card, model, { animate });
  }

  // =========================
  // Add-to-cart interception (pure)
  // =========================
  /**
   * Determine whether add-to-cart should be intercepted to open the gift modal.
   * Returns { intercept: true, reason: string } or { intercept: false }.
   */
  function shouldInterceptAddToCart({ isGiftWrapped, giftAlwaysOn, hasGiftConfig }) {
    const requiresGiftConfig = !!isGiftWrapped || !!giftAlwaysOn;
    if (!requiresGiftConfig) return { intercept: false };
    if (hasGiftConfig) return { intercept: false };
    return {
      intercept: true,
      reason: isGiftWrapped ? 'gift_wrapped_no_config' : 'gift_always_on_no_config',
    };
  }

  let checkoutClearDelegated = false;
  function bindCheckoutClearGiftConfig() {
    if (!global.document) return;
    if (checkoutClearDelegated) return;
    checkoutClearDelegated = true;
    document.body.dataset.scGiftClearDelegated = 'true';

    const selector = '[data-delivery-checkout-button], button[name="checkout"], input[name="checkout"]';
    document.body.addEventListener('click', (e) => {
      if (e.target.closest(selector)) {
        clearAllGiftConfigPrototypes();
      }
    });
  }

  // =========================
  // Gift summaries (cart drawer)
  // =========================
  let isRenderingGiftSummaries = false;

  function renderGiftSummaries(root = document) {
    if (isRenderingGiftSummaries) return;
    isRenderingGiftSummaries = true;

    try {
      let visited = 0;
      let updated = 0;
      root.querySelectorAll('[data-gift-config]').forEach((el) => {
        visited += 1;
        try {
          const raw = el.dataset.giftConfig;
          if (!raw) return;

          const parsed = safeParseGiftConfig(raw);
          if (!parsed.ok) return;

          const isGiftEditButton =
            typeof el.classList?.contains === 'function' && el.classList.contains('gift-edit-btn');

          // For the cart CTA button we keep the headline stable ("Gift note") and only
          // fill in the preview line. For legacy summary components, we still render
          // a compact title.
          const nextTitle = isGiftEditButton ? '' : formatGiftSummaryText(parsed.value);
          const rawMessage = isPlainObject(parsed.value)
            ? (parsed.value.message || '').toString().trim()
            : '';
          const nextPreview = rawMessage ? formatGiftMessagePreview(parsed.value) : 'Left blank';

          const titleEl =
            el.querySelector('[data-gift-summary-title]') ||
            el.querySelector('.gift-summary__title') ||
            el.querySelector('.gift-summary__text');

          if (!isGiftEditButton && titleEl && titleEl.textContent !== nextTitle) {
            titleEl.textContent = nextTitle;
            updated += 1;
          }

          const previewEl =
            el.querySelector('[data-gift-summary-preview]') ||
            el.querySelector('.gift-summary__preview');

          if (previewEl) {
            const wasHidden = !!previewEl.hidden;
            const shouldHide = !nextPreview;

            if (previewEl.textContent !== nextPreview) {
              previewEl.textContent = nextPreview;
              updated += 1;
            }

            if (wasHidden !== shouldHide) {
              previewEl.hidden = shouldHide;
              updated += 1;
            }
          }
        } catch (_) {
          // Keep default text
        }
      });

      debug('renderGiftSummaries', { visited, updated });
    } finally {
      isRenderingGiftSummaries = false;
    }
  }

  const api = {
    GIFT_CONFIG_SCHEMA_V1,

    // flags + debug
    isGiftUIEnabled,
    isDebugEnabled,
    debug,

    // config
    safeParseGiftConfig,
    serializeGiftConfig,
    summarizeGiftConfig,
    isGiftWrappedVariantTitle,

    getProductHandle,
    getGiftConfigStorageKey,
    giftConfigPrototype: {
      save: saveGiftConfigPrototype,
      load: loadGiftConfigPrototype,
      clear: clearGiftConfigPrototype,
      clearAll: clearAllGiftConfigPrototypes,
    },
    upsertGiftInputs,

    // add-to-cart interception
    shouldInterceptAddToCart,

    // card
    deriveGiftNoteCardModel,
    applyGiftNoteCardModel,
    updateGiftNoteCardState,
    updateGiftNoteCardStateScoped,

    // cart drawer
    renderGiftSummaries,
  };

  // Attach to global namespace.
  global.SnowcubesGift = global.SnowcubesGift || {};
  Object.assign(global.SnowcubesGift, api);

  // Backwards-compatible globals used elsewhere.
  if (!global.giftConfigPrototype) global.giftConfigPrototype = api.giftConfigPrototype;
  if (!global.isGiftUIEnabled) global.isGiftUIEnabled = api.isGiftUIEnabled;
  if (!global.updateGiftNoteCardState) global.updateGiftNoteCardState = api.updateGiftNoteCardState;

  if (global.document) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bindCheckoutClearGiftConfig);
    } else {
      bindCheckoutClearGiftConfig();
    }
  }

  // Jest / Node usage.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis);
